import { Bell } from 'lucide-react';
import BrandLogo from './BrandLogo';

// Mobile-native condensed dashboard.
//
// Why this exists separately from `DashboardMockup`:
//   The desktop dashboard is fixed-width (1100 px-ish) with a 210 px sidebar
//   and 6 table columns. Scaling that down to fit a 360 px phone viewport
//   either bleeds horizontally (cut on both sides) or shrinks text to ~5 px.
//   Mobile gets its own purpose-built layout: no sidebar, 4 essential columns,
//   native font sizes — so it FITS in the viewport AND stays legible.
//
// Keeps `data-dash-glass` so the existing `GlassTuner` runtime CSS targeting
// (frosted bg / blur tuning) continues to work without changes.

type Status = 'Live' | 'Scheduled' | 'Optimizing';

const rows: { owner: string; status: Status; campaign: string; metric: string }[] = [
  { owner: 'Alex K.', status: 'Live', campaign: 'Summer Launch — Reels', metric: 'CTR 4.8%' },
  { owner: 'Maria S.', status: 'Optimizing', campaign: 'Lookalike Retarget', metric: 'CPA $7.20' },
  { owner: 'Priya P.', status: 'Live', campaign: 'Brand Awareness', metric: 'Reach 1.2M' },
  { owner: 'Jamie L.', status: 'Scheduled', campaign: 'Newsletter — Weekly', metric: 'Open 41%' },
];

const statusStyles: Record<Status, string> = {
  Live: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30',
  Scheduled: 'bg-sky-500/15 text-sky-300 border border-sky-500/30',
  Optimizing: 'bg-amber-500/15 text-amber-300 border border-amber-500/30',
};

export default function DashboardMockupMobile() {
  return (
    <div
      data-dash-glass
      className="isolate w-full rounded-xl border border-white/[0.20] bg-black/25 backdrop-blur-md shadow-[0_24px_80px_-20px_rgba(0,0,0,0.9),inset_0_1px_0_rgba(255,255,255,0.14)] ring-1 ring-white/[0.07] overflow-hidden transform-gpu"
    >
      {/* Top bar — brand + section name + bell. No sidebar on mobile. */}
      <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-white/10">
        <BrandLogo className="h-4 w-4" />
        <span className="text-[12px] font-semibold tracking-tight text-white">
          SprynAI
        </span>
        <span className="ml-auto text-[10.5px] font-medium text-white/60">
          Campaign Overview
        </span>
        <button
          type="button"
          aria-hidden="true"
          tabIndex={-1}
          className="h-6 w-6 inline-flex items-center justify-center rounded-md border border-white/10 text-white/60"
        >
          <Bell className="h-3 w-3" />
        </button>
      </div>

      {/* Section title */}
      <div className="flex items-center justify-between px-3.5 pt-2.5 pb-2">
        <h4 className="text-[11.5px] font-medium text-white/90">
          Recent Activity Logs
        </h4>
        <span className="text-[9.5px] uppercase tracking-wider text-white/40">
          {rows.length} active
        </span>
      </div>

      {/* Table */}
      <div className="px-2 pb-2.5">
        <div className="rounded-lg border border-white/10 overflow-hidden">
          <div className="grid grid-cols-[0.85fr_0.7fr_1.45fr_0.9fr] gap-2 px-2.5 py-1.5 bg-white/[0.03] text-[9px] uppercase tracking-wider text-white/40">
            <span>Owner</span>
            <span>Status</span>
            <span>Campaign</span>
            <span className="text-right">Metric</span>
          </div>
          <div className="divide-y divide-white/5">
            {rows.map((r, i) => (
              <div
                key={i}
                className="grid grid-cols-[0.85fr_0.7fr_1.45fr_0.9fr] gap-2 items-center px-2.5 py-2 text-[11px] text-white/85"
              >
                <span className="text-white/75 truncate">{r.owner}</span>
                <span>
                  <span
                    className={
                      'inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium ' +
                      statusStyles[r.status]
                    }
                  >
                    {r.status}
                  </span>
                </span>
                <span className="truncate">{r.campaign}</span>
                <span className="text-right">{r.metric}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
